export interface OfficeHourEntry {
  day?: string | null;
  open_time?: string | null;
  close_time?: string | null;
}

export const DAY_ORDER = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;

export const DAY_FULL_LABELS: Record<string, string> = {
  mon: 'Monday',
  tue: 'Tuesday',
  wed: 'Wednesday',
  thu: 'Thursday',
  fri: 'Friday',
  sat: 'Saturday',
  sun: 'Sunday',
};

export const DAY_SHORT_LABELS: Record<string, string> = {
  mon: 'Mon',
  tue: 'Tue',
  wed: 'Wed',
  thu: 'Thu',
  fri: 'Fri',
  sat: 'Sat',
  sun: 'Sun',
};

function isBlankish(val: string | null | undefined): boolean {
  if (!val) return true;
  const normalized = val.trim().toLowerCase();
  return (
    normalized === '' ||
    normalized === 'false' ||
    normalized === 'null' ||
    normalized === 'undefined'
  );
}

export function normalizeDay(day: string): string {
  return day.trim().toLowerCase().slice(0, 3);
}

/** A day is closed unless both open and close times are real clock values. */
export function isClosedDay(entry: OfficeHourEntry): boolean {
  const open = entry.open_time?.trim().toLowerCase() ?? '';
  const close = entry.close_time?.trim().toLowerCase() ?? '';
  if (!open || !close) return true;
  if (open === 'closed' || close === 'closed') return true;
  if (['false', 'null', 'undefined'].includes(open)) return true;
  if (['false', 'null', 'undefined'].includes(close)) return true;
  return false;
}

/**
 * Return a Mon–Sun list. Weekdays missing from the feed are inserted as
 * closed so every hours display lists Saturday/Sunday (or any other
 * omitted day) instead of skipping them.
 *
 * Existing rows keep their original day-name spelling. Injected rows
 * match that style (Mon vs Monday). Unknown day names pass through at
 * the end so custom labels are not dropped.
 */
export function withClosedDays(hours: OfficeHourEntry[]): OfficeHourEntry[] {
  if (!hours.length) return hours;

  const sample = hours.find((entry) => entry.day && !isBlankish(entry.day))?.day?.trim() ?? '';
  const labels = sample.length > 0 && sample.length <= 3 ? DAY_SHORT_LABELS : DAY_FULL_LABELS;

  const byDay = new Map<string, OfficeHourEntry>();
  const unknown: OfficeHourEntry[] = [];

  for (const entry of hours) {
    if (!entry.day || isBlankish(entry.day)) continue;
    const key = normalizeDay(entry.day);
    if (!(DAY_ORDER as readonly string[]).includes(key)) {
      unknown.push(entry);
      continue;
    }
    if (!byDay.has(key)) byDay.set(key, entry);
  }

  const week = DAY_ORDER.map((key) => {
    return byDay.get(key) ?? { day: labels[key], open_time: null, close_time: null };
  });

  return unknown.length ? [...week, ...unknown] : week;
}
